import { Flex } from 'foundations-components/components/Flex';
import { DashboardPage } from 'foundations-components/transitional/components/DashboardPage';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { UsageScreenProvider, useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent, getPageViewedEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useEffect, useState } from 'react';
import { useHistory } from 'quartz-routing/react-router';
import { LazyImageProvider } from 'trellis-ecosystem';
import { SuggestedCollections } from '../collections/components/SuggestedCollections';
import { useContinueBrowsingRecorder } from '../components/ContinueBrowsingSection';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { SearchResultsPageHeader } from '../components/headers/SearchResultsPageHeader';
import { Paths } from '../constants/paths';
import { SEARCH_QUERY_CHAR_LIMIT } from '../constants/searchResults';
import { Screens, Subscreens } from '../constants/usage';
import { FilterPanel } from '../filters/components/FilterPanel';
import { FilterPanelButton } from '../filters/components/FilterPanelButton';
import { FilterTags } from '../filters/components/FilterTags';
import { StorefrontFilterBar } from '../filters/components/StorefrontFilterBar';
import { useFilterConfig, useRemoveInapplicableFilters } from '../filters/hooks/useFilterConfig';
import { useStorefrontFiltersInteractiveMode } from '../filters/interactiveMode/useStorefrontFiltersInteractiveMode';
import { useGates } from '../hooks/useGates';
import { ScrollToTopOnMount } from '../hooks/useScrollToTop';
import { ResultsGrid } from './components/ResultsGrid';
import { SearchResultsHeader } from './components/SearchResultsHeader';
import { SearchResultsPaginator } from './components/SearchResultsPaginator';
import { SortDropdown } from './components/SortDropdown';
import { useCollectionQuery } from './hooks/useCollectionQuery';
import { useSearchResultsQuery } from './hooks/useSearchResultsQuery';
import { SearchResultsPageMetadata } from './SearchResultsPageMetadata';
import { formatFiltersFromParams } from './utils/searchUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function SearchResultsPage() {
  return /*#__PURE__*/_jsx(UsageScreenProvider, {
    screen: Screens.SEARCH_RESULTS,
    children: /*#__PURE__*/_jsx(SearchResultsPageContent, {})
  });
}
function SearchResultsPageContent() {
  const {
    data,
    loading
  } = useSearchResultsQuery();
  const collectionResult = useCollectionQuery();
  const filterConfig = useFilterConfig();
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const {
    mobile
  } = useResponsiveProps({
    mobile: {
      xs: true,
      lg: false
    }
  });
  const gates = useGates();
  useContinueBrowsingRecorder();
  useRemoveInapplicableFilters();
  useStorefrontFiltersInteractiveMode({
    enabled: gates.breezeAssistant
  });
  const history = useHistory();
  const params = useSearchParams();
  const search = params.get('search');
  const sorts = params.get('sort');
  useEffect(() => {
    if (search && search.length > SEARCH_QUERY_CHAR_LIMIT) {
      const truncated = search.slice(0, SEARCH_QUERY_CHAR_LIMIT);
      params.set('search', truncated, {
        historyMethod: 'replace'
      });
    }
  }, [params, search]);
  useEffect(() => {
    if (!loading) {
      var _collectionResult$dat, _collectionResult$dat2;
      trackMarketplaceEvent(getPageViewedEvent(Object.assign({
        resultCount: data === null || data === void 0 ? void 0 : data.result.total,
        sorts: [sorts],
        filters: formatFiltersFromParams(params)
      }, !collectionResult.isUmbrellaCollection && {
        category: (_collectionResult$dat = collectionResult.data) === null || _collectionResult$dat === void 0 ? void 0 : _collectionResult$dat.slug,
        collectionId: (_collectionResult$dat2 = collectionResult.data) === null || _collectionResult$dat2 === void 0 ? void 0 : _collectionResult$dat2.id
      })));
    }
  }, [trackMarketplaceEvent, data, collectionResult, loading, sorts, params]);
  if (collectionResult.loading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      size: "medium",
      marginBlockStart: "1600"
    });
  }
  if (collectionResult.error || filterConfig.error) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      type: "badRequest"
    });
  }
  const hideSortAndFilter = gates.solutionsSpringSpotlight && history.location.pathname === Paths.SEARCH_RESULTS && !params.get('PRODUCT_TYPE');
  return /*#__PURE__*/_jsx(DashboardPage, {
    headerComponent: /*#__PURE__*/_jsx(SearchResultsPageHeader, {}),
    children: /*#__PURE__*/_jsxs(ErrorBoundary, {
      children: [/*#__PURE__*/_jsx(SearchResultsPageMetadata, {}), /*#__PURE__*/_jsx(ScrollToTopOnMount, {}), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "start",
        marginBlockStart: "200",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flex: 1,
          flexDirection: "column",
          gap: "600",
          maxInlineSize: "100%",
          minInlineSize: "0",
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "start",
            flexDirection: "column",
            flexWrap: "wrap",
            gap: "400",
            children: [/*#__PURE__*/_jsx(SearchResultsHeader, {}), !hideSortAndFilter && (mobile ? /*#__PURE__*/_jsxs(Flex, {
              alignItems: "start",
              gap: "800",
              inlineSize: "100%",
              marginBlockStart: "400",
              justifyContent: "space-between",
              children: [/*#__PURE__*/_jsx(SortDropdown, {}), /*#__PURE__*/_jsx(FilterPanelButton, {
                onFilterPanelOpen: () => setFilterPanelOpen(true)
              })]
            }) : /*#__PURE__*/_jsx(StorefrontFilterBar, {
              filterPanelOpen: filterPanelOpen,
              onFilterPanelOpen: () => setFilterPanelOpen(true)
            }))]
          }), /*#__PURE__*/_jsx(FilterTags, {}), /*#__PURE__*/_jsx(LazyImageProvider, {
            children: /*#__PURE__*/_jsx(ResultsGrid, {})
          })]
        }), !hideSortAndFilter && /*#__PURE__*/_jsx(FilterPanel, {
          open: filterPanelOpen,
          onClose: () => {
            trackMarketplaceEvent(getMarketplaceInteractionEvent({
              action: 'filter panel closed',
              subscreen: Subscreens.FILTER_PANEL
            }));
            setFilterPanelOpen(false);
          }
        })]
      }), /*#__PURE__*/_jsx(SearchResultsPaginator, {}), collectionResult.data && /*#__PURE__*/_jsx(SuggestedCollections, {})]
    })
  });
}